import { PaginatedResponse } from "@/types/common";
import { DoubleArrowLeftIcon, DoubleArrowRightIcon } from "@radix-ui/react-icons";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

export function ObjectListPagination<T>({ paginated_response, pageSizing = true }: { paginated_response: PaginatedResponse<T>; pageSizing?: boolean }) {
    const [searchParams, setSearchParams] = useSearchParams();

    const [pagination, setPagination] = useState({
        page: parseInt(searchParams.get("page") ?? "1"),
        pageSize: parseInt(searchParams.get("page_size") ?? "25"),
        pageMax: 1
    });

    useEffect(() => {
        setPagination({
            ...pagination,
            page: Math.max(1, Math.min(parseInt(searchParams.get("page") ?? "1"), pagination.page, Math.ceil(paginated_response.count / pagination.pageSize))),
            pageMax: Math.ceil(paginated_response.count / pagination.pageSize)
        });
    }, [paginated_response]);

    useEffect(() => {
        let _searchParams = new URLSearchParams(searchParams);
        if (parseInt(_searchParams.get("page") ?? "1") != pagination.page) {
            _searchParams.set("page", pagination.page.toString());
        }
        if (parseInt(_searchParams.get("page_size") ?? "25") != pagination.pageSize) {
            _searchParams.set("page_size", pagination.pageSize.toString());
        }
        if (_searchParams.toString() != searchParams.toString()) {
            setSearchParams(_searchParams);
        }
    }, [pagination]);

    return (
        <div className="flex w-full items-center justify-between">
            <div className="text-xs text-muted-foreground">
                Affichage de{" "}
                <strong>
                    {(pagination.page - 1) * pagination.pageSize + 1}-{paginated_response.results.length + (pagination.page - 1) * pagination.pageSize}
                </strong>{" "}
                sur <strong>{paginated_response.count}</strong> résultats
            </div>
            <div className="flex items-center space-x-6 lg:space-x-8">
                {pageSizing && (
                    <div className="flex items-center space-x-2">
                        <p className="text-xs">Éléments par page</p>
                        <Select
                            value={pagination.pageSize.toString()}
                            onValueChange={value => {
                                const pageSize = parseInt(value);
                                const pageMax = Math.ceil(paginated_response.count / pageSize);
                                const page = Math.min(pagination.page, pageMax);
                                setPagination({
                                    page: page,
                                    pageSize: pageSize,
                                    pageMax: pageMax
                                });
                            }}
                        >
                            <SelectTrigger className="h-6 w-[70px]">
                                <SelectValue placeholder={pagination.pageSize.toString()} />
                            </SelectTrigger>
                            <SelectContent side="top">
                                {[25, 50, 100].map(pageSize => (
                                    <SelectItem key={pageSize} value={`${pageSize}`}>
                                        {pageSize}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                )}

                <div className="flex items-center space-x-2">
                    <Button variant="outline" className="hidden h-6 w-6 p-0 lg:flex" onClick={() => setPagination({ ...pagination, page: 1 })} disabled={pagination.page <= 1}>
                        <span className="sr-only">Première page</span>
                        <DoubleArrowLeftIcon className="h-4 w-4" />
                    </Button>
                    <Button variant="outline" className="h-6 w-6 p-0" onClick={() => setPagination({ ...pagination, page: Math.max(pagination.page - 1, 1) })} disabled={pagination.page <= 1}>
                        <span className="sr-only">Page précédente</span>
                        <ChevronLeftIcon className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="outline"
                        className="h-6 w-6 p-0"
                        onClick={() => setPagination({ ...pagination, page: Math.min(pagination.page + 1, pagination.pageMax) })}
                        disabled={pagination.page >= pagination.pageMax}
                    >
                        <span className="sr-only">Page suivante</span>
                        <ChevronRightIcon className="h-4 w-4" />
                    </Button>
                    <Button
                        variant="outline"
                        className="hidden h-6 w-6 p-0 lg:flex"
                        onClick={() => setPagination({ ...pagination, page: pagination.pageMax })}
                        disabled={pagination.page >= pagination.pageMax}
                    >
                        <span className="sr-only">Dernière page</span>
                        <DoubleArrowRightIcon className="h-4 w-4" />
                    </Button>
                </div>
            </div>
        </div>
    );
}
